export type Role = 'user' | 'admin' | 'superuser';
export type UserProfileBadge = 'ADMIN' | 'CHEF' | 'NUTRITIONIST';
export type UserType = 'regular' | 'nutrition_consultant';
export type AccountMode = 'standard' | 'guided';
export type GuidedClientAccountStatus = 'pending' | 'active' | 'upgraded';
export type HouseholdRole = 'owner' | 'admin' | 'editor' | 'viewer';
export type Language = 'en' | 'he' | 'es' | 'ar' | 'de' | 'fr';
export type MealType = 'Breakfast' | 'Lunch' | 'Dinner';
export type ShoppingCategory = 'General' | 'Meat' | 'Vegetables' | 'Fruits' | 'Dairy' | 'Spices' | 'Snacks' | 'BabyProducts' | 'Cleaning' | 'Canned' | 'Pantry' | 'Toiletries' | 'GeneralFood' | 'Frozen' | 'Bakery' | 'Beverages' | 'Other';
export type WeekStartDay = 'Monday' | 'Sunday';
export type ThemeType = 'light' | 'dark' | 'system';
export type FontSizeMode = 'system' | 'custom';
export type SubscriptionStatus = 'pending' | 'subscribed' | 'declined' | 'canceled';
export type SuggestionArea = 'recipes' | 'shopping' | 'planner' | 'profile' | 'household' | 'other';
export type SuggestionStatus = 'new' | 'approved' | 'denied' | 'archive' | 'fulfilled';
export type ChefOriginLanguage = 'he' | 'en';
export type ChefRequestStatus = 'pending' | 'approved' | 'denied';
export type ChefRequestType = 'new' | 'existing';

/** Canonical allergy ids (EU-style major allergens + common triggers). */
export type KnownAllergyId =
    | 'peanuts'
    | 'treeNuts'
    | 'milk'
    | 'eggs'
    | 'wheat'
    | 'gluten'
    | 'soy'
    | 'fish'
    | 'shellfish'
    | 'sesame'
    | 'mustard'
    | 'celery'
    | 'sulphites'
    | 'lupin'
    | 'molluscs'
    | 'favaBeans';

/** User's personal nutrition diet preference (single choice). */
export type DietPreference =
    | 'none'
    | 'vegetarian'
    | 'keto'
    | 'vegan'
    | 'glutenFree'
    | 'paleo'
    | 'lowCarb'
    | 'dairyFree';

/** Diet labels a recipe can satisfy (multi-select, set by AI / recipe owner). */
export interface DietTypes {
    isKeto: boolean;
    isVegetarian: boolean;
    isVegan: boolean;
    isGlutenFree: boolean;
    isPaleo: boolean;
    isLowCarb: boolean;
    isDairyFree: boolean;
}

/** Lifestyle characteristics (multi-select). */
export interface LifestyleTags {
    isKidFriendly: boolean;
    isQuickPrep: boolean;
    isBudgetFriendly: boolean;
}

export interface IngredientAlternative {
    name: string;
    quantity: number;
    unit: string;
    isMeat?: boolean;
}

export interface Ingredient {
    name: string;
    quantity: number;
    unit: string;
    isMeat: boolean;
    section?: string;
    includedInShoppingList?: boolean;
    /** Substitute options (OR / slash-separated variants). Not added to shopping lists by default. */
    alternatives?: IngredientAlternative[];
}

export interface InstructionStep {
    id?: string;
    type: 'header' | 'step';
    content: string;
    /** Optional active-work duration in minutes for Gantt timeline planning. */
    durationMinutes?: number | null;
}

export type RecipeCategory = 'General' | 'First' | 'Meal' | 'Side' | 'Dessert' | 'Spread' | 'Snack';

export interface NutritionFactsPer100g {
    calories: number;
    fatGrams: number;
    fatPercentage: number;
    sodiumMg: number;
    dietaryFiberGrams: number;
    totalSugarsGrams: number;
    addedSugarsGrams: number;
    proteinGrams: number;
    calculatedAt?: string;
}

export interface Recipe {
    id: string;
    name: string;
    servings: number;
    prepTime?: number;
    ingredients: Ingredient[];
    instructions: string | InstructionStep[];
    links?: string[];
    originNote?: string;
    chefId?: string;
    isPublic?: boolean;
    sharedWithHouseholds?: string[];
    /**
     * Name-only placeholder. Stays private and can be shared only with households
     * the owner manages. Cleared once ingredients and instructions are saved.
     */
    isTemporary?: boolean;
    /** Source recipe when duplicated/adjusted for a specific household. */
    derivedFromRecipeId?: string;
    ownerId: string;
    householdId: string;
    category?: RecipeCategory;
    /** @deprecated Use dietTypes.isKeto — kept for reads until migration completes. */
    isKeto?: boolean;
    dietTypes?: DietTypes;
    lifestyleTags?: LifestyleTags;
    suitableFor?: MealType[];
    /** AI- or owner-tagged allergens detected in this recipe. Falls back to ingredient inference when absent. */
    allergens?: KnownAllergyId[];
    originLanguage?: Language;
    englishTranslation?: {
        name: string;
        ingredients: { name: string; unit: string; section?: string }[];
        instructions: InstructionStep[];
        nutritionFactsPer100g?: NutritionFactsPer100g;
    };
    nutritionFactsPer100g?: NutritionFactsPer100g;
    imageUrl?: string;
    /** Multiple recipe images served from GCS via /media/read. */
    imageUrls?: string[];
    /** Image ids aligned with imageUrls for recipe media objects. */
    imageMediaIds?: string[];
    /** Canonical source video id, e.g. instagram pk / media id or facebook reel id. */
    externalVideoId?: string;
    /** All known stable aliases for deduplication (pk, composite id, shortcode). */
    externalVideoIdAliases?: string[];
    isDeleted?: boolean;
    /**
     * When `pending`, the recipe is visible in the list but interactions are disabled
     * while nutrition / translation finalize in the background.
     */
    saveStatus?: 'pending' | 'ready';
    averageRating?: number;
    ratingCount?: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface PlannedMeal {
    id: string;
    recipeId: string;
    recipeName?: string;
    plannedServings: number;
    mealType?: MealType;
    sourcingStatus?: 'buy' | 'pantry' | 'prepared';
    isDeleted?: boolean;
}

export type DayOfWeek = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

export const DAYS_OF_WEEK: DayOfWeek[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const DAY_TRANSLATIONS: Record<DayOfWeek, string> = {
    'Sunday': 'day_Sunday',
    'Monday': 'day_Monday',
    'Tuesday': 'day_Tuesday',
    'Wednesday': 'day_Wednesday',
    'Thursday': 'day_Thursday',
    'Friday': 'day_Friday',
    'Saturday': 'day_Saturday'
};

export type MealPlan = {
    [key in DayOfWeek]?: PlannedMeal[];
} & {
    householdId?: string;
    weekStartDate?: string;
    creationDate?: string;
    slotMenuTitles?: Partial<Record<string, string>>;
};

export interface ShoppingCart {
    id: string;
    name: string;
    householdId: string;
    ownerId: string;
    ingredients: Ingredient[];
    category?: ShoppingCategory;
    isMisc?: boolean;
    isDeleted?: boolean;
}

export interface ActiveCartItem {
    id: string;
    name: string;
    unit: string;
    isMeat: boolean;
    done: boolean;
    category?: string;
    sources: {
        id: string;
        originName: string;
        quantity: number;
        originType: 'recipe' | 'manual' | 'list' | 'day' | 'sync';
        originId?: string;
        servings?: number;
        day?: string;
    }[];
}

export interface ActiveShoppingCart {
    householdId: string;
    items: ActiveCartItem[];
    collectionOrder?: string[];
    updatedAt: string;
}

export type ActiveShoppingCartUpdater = (
    latest: ActiveShoppingCart,
) => ActiveShoppingCart;

export type UpdateActiveShoppingCartFn = (
    cartOrUpdater: ActiveShoppingCart | ActiveShoppingCartUpdater,
) => Promise<void>;

export type ShoppingListType = Record<string, {
    name: string;
    quantity: number;
    unit: string;
    isMeat: boolean;
    category?: ShoppingCategory;
    sources?: {
        id?: string;
        recipeName: string;
        quantity: number;
        servings?: number;
        day?: string;
        originId?: string;
        originType?: 'day' | 'recipe' | 'list';
    }[];
}>;

export interface Subscription {
    status: SubscriptionStatus;
    startDate: string;
    endDate: string | null;
    plan: string;
    planDetails: { price: number; currency: string; households: number };
    adminViewed: boolean;
}

export interface User {
    uid: string;
    email: string;
    role: Role;
    /** Profile type — separate from system role (admin/superuser). */
    userType?: UserType;
    /** Superuser override for max owned households (e.g. nutrition consultants above 5). */
    householdEntitlement?: number;
    firstName?: string;
    lastName?: string;
    nickname?: string;
    activeHouseholdId?: string;
    households?: string[];
    language?: Language;
    weekStartDay?: WeekStartDay;
    theme?: ThemeType;
    subscription?: Subscription;
    creationDate?: string;
    updateDate?: string;
    acceptedTerms?: boolean;
    apiKey?: string;
    isAiEligible?: boolean;
    /** Personal nutrition diet; default is none. */
    dietPreference?: DietPreference;
    /** AES-256-GCM encrypted personal Gemini API key (encrypted server-side). */
    encryptedGeminiKey?: string;
    /** Per-household expanded active-cart section ids (pillar ids the user left open). */
    activeCartExpandedSections?: Record<string, string[]>;
    /** Personal diet types — multi-select flags the user eats by. */
    userDietTypes?: DietTypes;
    /** Set when superuser approves chef recognition request; read-only for user. */
    chefId?: string;
    /** Guided client program — limited account until Go Pro upgrade. */
    accountMode?: AccountMode;
    guidedClientId?: string;
    /** Optional profile image for consultants and users. */
    avatarUrl?: string;
    /** Shared media folder id for avatar/cover uploads. */
    mediaId?: string;
    /** Verified profile badges shown on the user profile header. */
    badges?: UserProfileBadge[];
    /** Public profile description shown on personal details. */
    profileBio?: string;
    /** Consultant credential label (e.g. Certified Dietitian). */
    consultantCertification?: string;
    /** Cover photo shown on the personal profile header. */
    coverPhotoUrl?: string;
    knownAllergies?: KnownAllergyId[];
    /** When true, show intermittent fasting controls in the header. */
    intermittentFastingEnabled?: boolean;
    allowHouseholdInvitations?: boolean;
    birthday?: string;
}

export type RecipeContentRejectionCode = 'not_a_recipe' | 'inappropriate_content';

export type MisconductSource = 'recipe_analyze' | 'recipe_save';

export interface MisconductUsage {
    id?: string;
    userId: string;
    severity: number;
    code: RecipeContentRejectionCode;
    source: MisconductSource;
    createdAt: string;
    contentLength?: number;
}

export interface GuidedClient {
    id: string;
    email: string;
    firstName: string;
    lastName?: string;
    nickname?: string;
    knownAllergies?: KnownAllergyId[];
    dietPreference?: DietPreference;
    userDietTypes?: DietTypes;
    userUid?: string | null;
    accountStatus: GuidedClientAccountStatus;
    createdAt: string;
    updatedAt: string;
    /** UID of the admin/superuser who first created the record. */
    createdByUid: string;
    /** All nutrition consultants linked to this guided client. */
    consultantUids?: string[];
}

export interface GuidedClientAccess {
    id: string;
    clientId: string;
    consultantId: string;
    householdId: string;
    tokenHash: string;
    tokenVersion: number;
    isActive: boolean;
    createdAt: string;
    refreshedAt?: string;
}

export interface ConsultantClientContext {
    clientId: string;
    accessId: string;
    householdId: string;
    clientName: string;
}

export interface ConsultationThread {
    id: string;
    clientId: string;
    consultantId: string;
    clientUid: string;
    consultantUid: string;
    participantIds: string[];
    householdId?: string;
    status: 'open' | 'closed';
    lastMessageAt: string;
    lastMessageFrom?: 'client' | 'consultant';
    consultantReadAt?: string;
    clientReadAt?: string;
    createdAt: string;
}

export interface ConsultantPhrase {
    id: string;
    consultantId: string;
    phrase: string;
    createdAt: string;
    expiresAt: string;
    isCurrent: boolean;
}

export interface HouseholdPlanMeta {
    householdId: string;
    lastUpdatedAt: string;
    lastUpdatedByUid: string;
}

export interface ConsultationMessage {
    id: string;
    from: 'client' | 'consultant';
    text: string;
    createdAt: string;
    senderUid: string;
}

export type MealPreferenceSource = 'manual' | 'inferred';

export interface MealPreferenceEntry {
    addedAt: string;
    source: MealPreferenceSource;
}

export interface UserMealPreferencesFavorites {
    recipes: Record<string, MealPreferenceEntry>;
    chefs: Record<string, MealPreferenceEntry>;
}

export interface UserInferredMealPreferences {
    lastComputedAt?: string;
    recipeScores?: Record<string, { score: number; signals: string[]; updatedAt: string }>;
    chefScores?: Record<string, { score: number; signals: string[]; updatedAt: string }>;
}

export interface UserMealPreferences {
    userId: string;
    favorites: UserMealPreferencesFavorites;
    preferredDiet: DietPreference;
    preferredDietTypes?: DietTypes;
    inferred?: UserInferredMealPreferences;
    createdAt: string;
    updatedAt: string;
}

export interface AiMealPreferencesInput {
    favoriteRecipeIds: string[];
    favoriteChefIds: string[];
    preferredDiet: DietPreference;
    preferredDietTypes: DietTypes;
    inferredRecipeIds?: string[];
    inferredChefIds?: string[];
}

export interface Chef {
    id: string;
    name: string;
    englishName: string;
    nickname: string;
    originLanguage: ChefOriginLanguage;
    originCountry?: string;
    /** @deprecated Legacy single link. Prefer `websites`; kept for backward compatibility. */
    website?: string;
    /** Multiple chef links (website, Instagram, other social profiles). */
    websites?: string[];
    /** Short bio / tagline (e.g. parsed from Instagram full_name). */
    description?: string;
    /** Instagram handle for profile refresh (without @). */
    instagramUsername?: string;
    /** Instagram numeric user id (pk). */
    instagramUserId?: string;
    imageUrl?: string;
    linkedUserId?: string;
    disabled?: boolean;
    createdAt?: string;
    updatedAt?: string;
}

export interface ChefRequest {
    id: string;
    userId: string;
    userEmail: string;
    requestType: ChefRequestType;
    targetChefId?: string;
    proposedName?: string;
    proposedEnglishName?: string;
    website?: string;
    message?: string;
    status: ChefRequestStatus;
    createdAt: string;
    reviewedAt?: string;
    linkedChefId?: string;
}

export interface Household {
    id: string;
    name: string;
    ownerId: string;
    members: string[];
    memberRoles?: Record<string, HouseholdRole>;
    residentsCount?: number;
    adultsCount?: number;
    childrenCount?: number;
    visibleMealTypes?: MealType[];
    guidedClientId?: string;
    isGuidedClientHousehold?: boolean;
    invitedMembers?: string[];
    rejectedMembers?: Record<string, number>;
    tenants?: Tenant[];
    memberIsTenant?: Record<string, boolean>;
    memberIsChild?: Record<string, boolean>;
}

export interface Tenant {
    id: string;
    name: string;
    dietTypes?: DietTypes;
    knownAllergies?: KnownAllergyId[];
    isChild?: boolean;
}

export interface Review {
    id: string;
    recipeId: string;
    userId: string;
    rating: number;
    createdAt: string;
    updatedAt: string;
}

export interface Suggestion {
    id: string;
    subject: string;
    text: string;
    area: SuggestionArea;
    date: string;
    status: SuggestionStatus;
    reporterUserId: string;
    reporterEmail: string;
}

export interface Reminder {
    id: string;
    text: string;
    date: string;
    type: 'order' | 'defrost';
    meatItems: { name: string; quantity: number; unit: string; recipeName: string }[];
}

export type UserNotificationType =
    | 'share_import_started'
    | 'share_import_success'
    | 'share_import_failure'
    | 'share_import_duplicate';

export interface UserNotification {
    id: string;
    type: UserNotificationType;
    title: string;
    body: string;
    recipeId?: string;
    read: boolean;
    createdAt: string;
}

export interface FastingSession {
    id: string;
    startedAt: string;
    endedAt?: string;
    startDate: string;
    durationMinutes?: number;
    goalHours?: number;
    status: 'scheduled' | 'active' | 'completed';
    /** When status is scheduled, the future time the fast should begin. */
    scheduledStartAt?: string;
}

export interface AnalyzedPrepStep {
    stepIndex: number;
    content: string;
    sectionHeader: string;
    activeMinutes: number;
    waitAfterMinutes: number;
    waitDescription?: string;
}

export interface AnalyzedRecipeTimeline {
    recipeId: string;
    recipeName: string;
    steps: AnalyzedPrepStep[];
    totalActiveMinutes: number;
    totalElapsedMinutes: number;
}

export interface AnalyzePrepTimelineResponse {
    recipes: AnalyzedRecipeTimeline[];
}

export type PrepTimelineMode = 'startNow' | 'targetTime' | 'session';

export interface ScheduledPrepSegment {
    recipeId: string;
    recipeName: string;
    stepIndex: number;
    content: string;
    sectionHeader: string;
    startMinutes: number;
    endMinutes: number;
    durationMinutes: number;
    kind: 'active' | 'wait';
    waitDescription?: string;
}

export interface ScheduledRecipeTimeline {
    recipeId: string;
    recipeName: string;
    startMinutes: number;
    endMinutes: number;
    totalMinutes: number;
    segments: ScheduledPrepSegment[];
}
